import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Select, Space, Spin, Typography, message } from "antd";
import dayjs from "dayjs";
import {
  getSalesPriceLimitStatus,
  updateSalesPriceLimitStatus,
} from "../../api/settingsApi";
import { ISalesPriceLimitStatus } from "../../types/settings.type";

const FALLBACK_TIMER_OPTIONS = [30, 60];

function formatDuration(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [hours, minutes, seconds]
    .map((value) => value.toString().padStart(2, "0"))
    .join(":");
}

function getStatusText(status: ISalesPriceLimitStatus): string {
  if (!status.effectiveEnabled && status.mode === "disabled") {
    return "Отключен бессрочно";
  }

  if (!status.effectiveEnabled && status.mode === "timer") {
    return "Отключен по таймеру";
  }

  return "Включен";
}

export default function SalesPriceLimitSettings() {
  const [status, setStatus] = useState<ISalesPriceLimitStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [durationMinutes, setDurationMinutes] = useState<number>(30);
  const [nowMs, setNowMs] = useState<number>(Date.now());

  const loadStatus = useCallback(async () => {
    try {
      const nextStatus = await getSalesPriceLimitStatus();
      setStatus(nextStatus);

      setDurationMinutes((prev) => {
        if (nextStatus.availableDurations.length > 0 && !nextStatus.availableDurations.includes(prev)) {
          return nextStatus.availableDurations[0];
        }
        return prev;
      });
    } catch (error: any) {
      message.error(error.response?.data?.error || "Ошибка при загрузке статуса лимита продаж");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    const pollId = window.setInterval(loadStatus, 30000);

    return () => {
      window.clearInterval(pollId);
    };
  }, []);

  useEffect(() => {
    const ticker = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(ticker);
    };
  }, []);

  const availableDurations = useMemo(() => {
    if (!status?.availableDurations?.length) {
      return FALLBACK_TIMER_OPTIONS;
    }

    return status.availableDurations;
  }, [status]);

  const remainingSeconds = useMemo(() => {
    if (!status?.pausedUntil || !status.timerActive) {
      return 0;
    }

    const pausedUntilMs = new Date(status.pausedUntil).getTime();
    return Math.max(0, Math.floor((pausedUntilMs - nowMs) / 1000));
  }, [status, nowMs]);

  const updateMode = async (mode: "enabled" | "disabled" | "timer") => {
    setActionLoading(true);
    try {
      const payload =
        mode === "timer" ? { mode, durationMinutes } : { mode };
      const nextStatus = await updateSalesPriceLimitStatus(payload);
      setStatus(nextStatus);

      const successMessageByMode: Record<typeof mode, string> = {
        enabled: "Лимит продаж включен",
        disabled: "Лимит продаж отключен бессрочно",
        timer: `Лимит продаж отключен на ${durationMinutes} мин`,
      };

      message.success(successMessageByMode[mode]);
    } catch (error: any) {
      message.error(error.response?.data?.error || "Ошибка при обновлении лимита продаж");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <Spin tip="Загрузка статуса лимита продаж..." />;
  }

  if (!status) {
    return <Alert message="Не удалось загрузить статус лимита продаж" type="error" showIcon />;
  }

  return (
    <Card title="Лимит продаж цены" style={{ width: 500, marginBottom: 16 }}>
      <Typography.Paragraph style={{ marginBottom: 8 }}>
        <strong>Статус:</strong> {getStatusText(status)}
      </Typography.Paragraph>

      {status.timerActive && status.pausedUntil && (
        <Typography.Paragraph style={{ marginBottom: 16 }}>
          <strong>Отключен до:</strong> {dayjs(status.pausedUntil).format("DD.MM.YYYY HH:mm:ss")}
          <br />
          <strong>Осталось:</strong> {formatDuration(remainingSeconds)}
        </Typography.Paragraph>
      )}

      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <Space>
          <Button
            type="primary"
            onClick={() => updateMode("enabled")}
            loading={actionLoading}
            disabled={status.effectiveEnabled}
          >
            Включить лимит
          </Button>
          <Button
            onClick={() => updateMode("disabled")}
            loading={actionLoading}
            disabled={!status.effectiveEnabled && status.mode === "disabled"}
          >
            Отключить бессрочно
          </Button>
        </Space>

        <Space>
          <Select
            value={durationMinutes}
            style={{ width: 170 }}
            options={availableDurations.map((value) => ({
              label: `${value} минут`,
              value,
            }))}
            onChange={(value) => setDurationMinutes(value)}
            disabled={actionLoading}
          />
          <Button onClick={() => updateMode("timer")} loading={actionLoading}>
            Отключить с таймером
          </Button>
        </Space>
      </Space>
    </Card>
  );
}
